import React, { useState, useMemo } from 'react';
import { useEmendasData } from '../../hooks/emendas/useEmendasData';
import { useAllEmendasData } from '../../hooks/emendas/useAllEmendasData';
import { useGlobalSearch } from '../../hooks/emendas/useGlobalSearch';
import { useAdvancedFilters } from '../../hooks/emendas/useAdvancedFilters';
import { useYearFilter } from '../../hooks/emendas/useYearFilter';
import { usePagination } from '../../hooks/emendas/usePagination';
import { useExcelExport } from '../../hooks/emendas/useExcelExport';
import { calculateStatistics, calculateUnifiedStatistics } from '../../utils/emendas/statisticsHelpers';
import { EMENDA_TYPES } from '../../utils/emendas/constants';

import EmendaTabs from './EmendaTabs/EmendaTabs';
import StatisticsCard from './StatisticsCard/StatisticsCard';
import GlobalSearch from './GlobalSearch/GlobalSearch';
import AdvancedFiltersButton from './AdvancedFilters/AdvancedFiltersButton';
import AdvancedFiltersDrawer from './AdvancedFilters/AdvancedFiltersDrawer';
import ViewModeToggle from './ViewControls/ViewModeToggle';
import ExportDropdown from './ViewControls/ExportDropdown';
import CardsView from './CardsView/CardsView';
import TableView from './TableView/TableView';
import EmendaDetailsModal from './EmendaDetailsModal/EmendaDetailsModal';
import SkeletonLoader from './SkeletonLoader/SkeletonLoader';

import './EmendasDisplay.css';

export default function EmendasDisplay() {
  // Recuperar aba ativa do localStorage ou usar MUNICIPAL como padrão
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('emendas_active_tab');
    return savedTab && Object.values(EMENDA_TYPES).includes(savedTab) 
      ? savedTab 
      : EMENDA_TYPES.MUNICIPAL;
  });
  
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const { data, columns, loading, error, emendaType } = useEmendasData(activeTab);
  
  // Carregar dados de ambos os tipos para estatísticas unificadas
  const allEmendasData = useAllEmendasData();

  // Salvar aba ativa no localStorage sempre que mudar
  React.useEffect(() => {
    localStorage.setItem('emendas_active_tab', activeTab);
  }, [activeTab]);
  
  const [viewMode, setViewMode] = useState('cards');
  const [globalSearch, setGlobalSearch] = useState('');
  const [advancedFilters, setAdvancedFilters] = useState({});
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedEmenda, setSelectedEmenda] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const searchedData = useGlobalSearch(data, globalSearch);
  const { filteredData: advancedFilteredData, activeFiltersCount } = useAdvancedFilters(searchedData, advancedFilters);
  const { selectedYear, setSelectedYear, filteredData } = useYearFilter(advancedFilteredData, columns);
  
  const {
    paginatedData,
    currentPage,
    totalPages,
    itemsPerPage,
    setCurrentPage
  } = usePagination(filteredData, isMobile);


  // Resetar filtros quando activeTab mudar
  React.useEffect(() => {
    setGlobalSearch('');
    setAdvancedFilters({});
    setSelectedYear('all');
    setCurrentPage(1);
    setSelectedEmenda(null);
    setShowAdvancedFilters(false);
  }, [activeTab]);

  const { handleExportExcel, handleExportTXT } = useExcelExport(filteredData, columns, emendaType);

  const statistics = useMemo(() => {
    // Usar estatísticas unificadas que consideram anos de ambos os tipos
    if (activeTab === EMENDA_TYPES.MUNICIPAL) {
      return calculateUnifiedStatistics(
        data,
        columns,
        allEmendasData.estadualData,
        allEmendasData.estadualColumns
      );
    } else {
      return calculateUnifiedStatistics(
        data,
        columns,
        allEmendasData.municipalData,
        allEmendasData.municipalColumns
      );
    }
  }, [data, columns, activeTab, allEmendasData.municipalData, allEmendasData.municipalColumns, allEmendasData.estadualData, allEmendasData.estadualColumns]);

  // Marcar que carregamento inicial foi concluído
  React.useEffect(() => {
    if (!loading && data.length > 0) {
      setIsInitialLoad(false);
    }
  }, [loading, data]);

  // Pré-carregar a outra aba em background após carregar a primeira
  React.useEffect(() => {
    if (!loading && data.length > 0) {
      // Aguardar 2 segundos antes de pré-carregar (não bloquear UI)
      const timer = setTimeout(() => {
        import('../../hooks/emendas/useEmendasData').then(module => {
          module.EmendasCacheManager.preloadAll();
        });
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [loading, data, activeTab]);

  // Função para trocar de aba
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
  };

  // Mostrar skeleton apenas no carregamento inicial
  if (loading && isInitialLoad) {
    return <SkeletonLoader />;
  }

  if (error) {
    return (
      <div className="emendas-display">
        <EmendaTabs 
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
        <div className="error-container">
          <h2>Erro ao carregar dados</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="emendas-display">
      <EmendaTabs 
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

      {loading && !isInitialLoad && (
        <div className="loading-overlay">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Carregando dados...</p>
          </div>
        </div>
      )}

      <div key={`emenda-content-${activeTab}`}>
        <StatisticsCard
          totalValue={statistics.totalValue}
          totalCount={statistics.totalCount}
          yearRange={statistics.yearRange}
          yearBreakdown={statistics.yearBreakdown}
          selectedYear={selectedYear}
          onYearSelect={setSelectedYear}
        />

        <div className="emendas-display__header">
          <GlobalSearch
            value={globalSearch}
            onChange={setGlobalSearch}
            resultsCount={filteredData.length}
          />

          <AdvancedFiltersButton
            activeCount={activeFiltersCount}
            onClick={() => setShowAdvancedFilters(true)}
          />
        </div>

        <div className="emendas-display__controls">
          <div className="controls-left">
            <p className="results-info">
              {paginatedData.length > 0 ? (
                <>
                  {(currentPage - 1) * itemsPerPage + 1}-
                  {Math.min(currentPage * itemsPerPage, filteredData.length)} de {filteredData.length} emendas
                </>
              ) : (
                'Nenhuma emenda encontrada'
              )}
            </p>
          </div>

          <div className="controls-right">
            <ViewModeToggle
              mode={viewMode}
              onChange={setViewMode}
            />

            <ExportDropdown
              show={showExportMenu}
              onToggle={() => setShowExportMenu(!showExportMenu)}
              onExportExcel={handleExportExcel}
              onExportTXT={handleExportTXT}
              count={filteredData.length}
            />
          </div>
        </div>

        <div className="emendas-display__content">
          {viewMode === 'cards' ? (
            <CardsView
              data={paginatedData}
              columns={columns}
              onViewDetails={setSelectedEmenda}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          ) : (
            <TableView
              data={paginatedData}
              columns={columns}
              onViewDetails={setSelectedEmenda}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      </div>

      {showAdvancedFilters && (
        <AdvancedFiltersDrawer
          columns={columns}
          filters={advancedFilters}
          onChange={setAdvancedFilters}
          onClose={() => setShowAdvancedFilters(false)}
        />
      )}

      {selectedEmenda && (
        <EmendaDetailsModal
          emenda={selectedEmenda}
          columns={columns}
          onClose={() => setSelectedEmenda(null)}
        />
      )}
    </div>
  );
}
